import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { abortChatTurn, sendChatTurn } from '@/slices/chat/sendChatTurn';
import {
  selectActiveConversationId,
  selectConversation,
  selectIsChatLimitReached,
  selectRegenerateTargetId,
  type ChatRootState,
} from '@/slices/chat/chatSelectors';
import type { ChatStreamDeps } from '@/services/chatStream/chatStreamClient';
import type { ChatStreamOutcome } from '@/services/chatStream/chatStreamClient';
import type { ChatTurnAttachment, ChatTurnRequest } from '@/types/chatStream';
import type { ChatMessageAttachment } from '@/slices/chat/chatSlice';
import type { ChatAppContextEnvelope } from '@/types/chatEnvelope';
import type { AppDispatch } from '@/store/store';
import { retainChatPreviews } from '@/slices/chat/chatPreviewLifetime';
import { planAttachmentRetry } from './chatAttachmentRetry';
import { requestChatFollowups } from '@/services/chatFollowups';
import { followupsReceived } from '@/slices/chat/chatSlice';
import {
  captureCompanionMessageSent,
  type CompanionAnalyticsSurface,
  type CompanionSendEntry,
} from './companionAnalytics';

/** What a caller has to say to start a turn. */
export interface ChatSendInput {
  message: string;
  /** Composer uploads are complete wire refs; hydrated rows never call send. */
  attachments?: Array<ChatMessageAttachment & ChatTurnAttachment>;
  documentIds?: string[];
  /** Display metadata for documents in this live user message, never on wire. */
  documentAttachments?: ChatMessageAttachment[];
  /** A caller-owned context snapshot, for a turn linked from served content. */
  envelope?: ChatAppContextEnvelope;
  /** How the student asked, for `companion_message_sent` (ORA-1118). */
  entry?: CompanionSendEntry;
}

export interface UseChatSendOptions {
  /** Null starts a new conversation; the server assigns the id on `turn.start`. */
  conversationId: string | null;
  /**
   * The app-context envelope for this surface (ORA-449). The shells read it
   * from `useAppContextEnvelope` and pass it through here, so the envelope on
   * the wire is the one the student was looking at when they pressed send —
   * not whatever the published fallback happens to hold a microtask later.
   */
  getEnvelope?: () => ChatAppContextEnvelope | undefined;
  /**
   * Stream transport override. Production leaves this undefined and the thunk
   * builds the real deps; the dev fixture page and the tests inject their own.
   */
  streamDeps?: ChatStreamDeps;
  /** A changed anchor is resolving; do not post into the old thread. */
  isThreadResolving?: boolean;
  /** Ask the backend to create a distinct thread for this first turn. */
  forceNewConversation?: boolean;
  /**
   * The shell this send path belongs to, for `companion_message_sent`
   * (ORA-1118). Omitted, the send is not reported.
   */
  analyticsSurface?: CompanionAnalyticsSurface;
}

export interface UseChatSend {
  /**
   * The conversation the caller should render. When the caller starts from
   * `null`, this becomes the conversation the first turn created — including
   * after `turn.start` re-keys it from the draft id to the server's — and only
   * for as long as the slice is still on that thread (ORA-581).
   */
  conversationId: string | null;
  send: (input: ChatSendInput) => Promise<ChatStreamOutcome | null>;
  /** Starts a valid turn synchronously and says whether the composer may clear. */
  sendIfAccepted: (input: ChatSendInput) => boolean;
  /**
   * Re-run the turn that produced this assistant message. Retry (after a
   * failure) and regenerate (after a good answer) are the same request: the
   * original user text, plus `regenerateOf`. It appends a new turn to the same
   * conversation rather than editing the old one.
   */
  retry: (assistantMessageId: string) => Promise<ChatStreamOutcome | null>;
  /** Stop the turn that is streaming. Partial text stays on screen. */
  stop: () => void;
  isStreaming: boolean;
  /**
   * A turn cannot be opened right now for a reason that will pass: one is
   * streaming, the one before it has not finished settling, or the anchor's
   * thread is still resolving. The reactive form of what `canOpenTurn` checks,
   * for a caller that must wait rather than be refused — `isStreaming` alone
   * goes false the moment `turn.done` lands, while the send path stays shut
   * until the turn's own promise settles a few microtasks later.
   */
  isBusy: boolean;
  /** The daily allowance is spent; sending and retrying are both refused. */
  isLimitReached: boolean;
  /**
   * Why the last send was refused outright, for the composer to say inline
   * (ORA-750). Only the message-length refusal reaches here: it is answered at
   * the composer because the student has to edit what they wrote, unlike every
   * other failure, which belongs to the turn's own notice in the thread. Cleared
   * when the next turn opens.
   */
  sendRejection: string | null;
}

/** The user message immediately above an assistant message in the thread. */
const findPrecedingUserMessage = (
  state: ChatRootState,
  conversationId: string,
  assistantMessageId: string
) => {
  const messageIds = state.chat.conversations[conversationId]?.messageIds ?? [];
  const index = messageIds.indexOf(assistantMessageId);

  for (let i = index - 1; i >= 0; i -= 1) {
    const candidate = state.chat.messages[messageIds[i]];
    if (candidate?.role === 'user') return candidate;
  }

  return null;
};

/**
 * The one send path for the AI companion. The composer uses it today; the
 * follow-up chips, preset chips, and selection prefill build the same request
 * through it as their tickets land, so there is never a second way to open a
 * turn.
 */
export const useChatSend = ({
  conversationId,
  getEnvelope,
  streamDeps,
  isThreadResolving = false,
  forceNewConversation = false,
  analyticsSurface,
}: UseChatSendOptions): UseChatSend => {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<ChatRootState>();
  /**
   * The turn this hook last opened. A caller starting a new conversation has
   * no id to follow, and the id the slice assigns is a draft that `turn.start`
   * later re-keys — so the turn is the stable handle, and the conversation is
   * read back off it.
   */
  const [openedTurnId, setOpenedTurnId] = useState<string | null>(null);

  const openedTurnConversationId = useSelector((state: ChatRootState) =>
    openedTurnId
      ? (state.chat.turns[openedTurnId]?.conversationId ?? null)
      : null
  );

  const activeConversationId = useSelector(selectActiveConversationId);

  /**
   * The conversation the opened turn belongs to — but only while the slice is
   * still pointing the surfaces at it (ORA-581).
   *
   * Without that condition the fallback outlives the thread it was opened for:
   * the drawer stays mounted across a whole QBank block, so the student chats
   * on question 1, moves to question 2, the resolver repoints the thread — and
   * this hook, asked for `null`, went on handing back question 1's
   * conversation, which is the one the drawer kept showing. Every deliberate
   * thread move (an anchor change, a history choice, New Chat) sets the slice's
   * active conversation, so comparing against it is what makes the fallback
   * expire with the turn's own thread.
   */
  const openedConversationId =
    openedTurnConversationId !== null &&
    openedTurnConversationId === activeConversationId
      ? openedTurnConversationId
      : null;

  const effectiveConversationId = conversationId ?? openedConversationId;

  // The turn *id*, not the turn: every event bumps the turn's `lastSeq`, so
  // subscribing to the object would re-render this hook's consumers — and with
  // them the whole thread — once per streamed delta.
  const activeTurnId = useSelector(
    (state: ChatRootState) =>
      selectConversation(state, effectiveConversationId)?.activeTurnId ?? null
  );

  /**
   * The turn this hook opened, still running on a thread the view has since
   * moved off (the student turned the page mid-answer — ORA-581). It is the
   * same turn as `activeTurnId` whenever the view is on its conversation.
   *
   * Reported as streaming because it is: `canOpenTurn` refuses while it runs,
   * so a composer that looked ready would take what the student typed and
   * quietly drop it.
   */
  const openedTurnIsRunning = useSelector(
    (state: ChatRootState) =>
      openedTurnId !== null &&
      openedTurnConversationId !== null &&
      selectConversation(state, openedTurnConversationId)?.activeTurnId ===
        openedTurnId
  );

  const streamingTurnId =
    activeTurnId ?? (openedTurnIsRunning ? openedTurnId : null);

  const isLimitReached = useSelector(selectIsChatLimitReached);
  /**
   * `send` and `retry` read the guards through a ref as well as the render
   * value: two clicks in the same tick both see the pre-click render, and the
   * second one must still be refused.
   */
  const inFlightRef = useRef(false);
  const followupRequests = useRef(new Map<string, AbortController>());
  /** The observable half of `inFlightRef`, for `isBusy`. */
  const [isTurnSettling, setIsTurnSettling] = useState(false);
  const [sendRejection, setSendRejection] = useState<string | null>(null);

  useEffect(
    () => () => {
      followupRequests.current.forEach((controller) => controller.abort());
      followupRequests.current.clear();
    },
    []
  );

  const dispatchTurn = useCallback(
    (
      request: ChatTurnRequest,
      ids?: {
        userMessageId?: string;
        supersedes?: string[];
        userMessageAttachments?: ChatMessageAttachment[];
      }
    ): Promise<ChatStreamOutcome> => {
      const turnId = uuidv4();
      inFlightRef.current = true;
      setIsTurnSettling(true);
      setOpenedTurnId(turnId);
      // Whatever the last send was refused for, this one is a fresh answer to it.
      setSendRejection(null);

      const completion = dispatch(
        sendChatTurn({
          request,
          turnId,
          userMessageId: ids?.userMessageId,
          ...(ids?.userMessageAttachments
            ? { userMessageAttachments: ids.userMessageAttachments }
            : {}),
          ...(ids?.supersedes ? { supersedes: ids.supersedes } : {}),
          deps: streamDeps,
        })
      ).finally(() => {
        inFlightRef.current = false;
        setIsTurnSettling(false);
      });

      /**
       * A refusal the composer has to answer rather than the thread (ORA-750).
       * The turn itself is already gone — `sendChatTurn` discards it, because a
       * message the route would not take produced no answer to show — so the
       * only place left to say why is beside the box the text went back into.
       */
      void completion.then((outcome) => {
        if (outcome.failure?.code === 'message_too_long') {
          setSendRejection(outcome.failure.message);
        }
      });

      // `turn.done` has already cleared the active turn before this continuation
      // runs. Do not await it: the user can send the next message while chips
      // are generated for the answer they just read.
      void completion.then(() => {
        const turn = store.getState().chat.turns[turnId];

        const assistant = turn
          ? store.getState().chat.messages[turn.assistantMessageId]
          : null;

        if (
          !turn ||
          turn.status !== 'done' ||
          !assistant?.serverId ||
          followupRequests.current.has(turnId)
        ) {
          return;
        }

        const messageId = assistant.serverId;
        const controller = new AbortController();
        followupRequests.current.set(turnId, controller);

        void requestChatFollowups(messageId, controller.signal)
          .then((result) => {
            if (result?.status !== 'complete') return;

            dispatch(
              followupsReceived({
                turnId,
                messageId,
                items: result.items,
              })
            );
          })
          .finally(() => followupRequests.current.delete(turnId));
      });

      retainChatPreviews(store);
      return completion;
    },
    [dispatch, streamDeps, store]
  );

  /** A turn may only start when nothing is streaming and the user has chats left. */
  const canOpenTurn = useCallback(
    () =>
      !inFlightRef.current &&
      activeTurnId === null &&
      !isLimitReached &&
      !isThreadResolving,
    [activeTurnId, isLimitReached, isThreadResolving]
  );

  const send = useCallback(
    async (input: ChatSendInput) => {
      const message = input.message.trim();
      if (
        (!message &&
          !input.attachments?.length &&
          !input.documentIds?.length) ||
        !canOpenTurn()
      )
        return null;

      const envelope = input.envelope ?? getEnvelope?.();

      // Reported once the turn is going out, so a refused send (one streaming,
      // the cap spent) is not counted as a message.
      if (analyticsSurface) {
        captureCompanionMessageSent({
          surface: analyticsSurface,
          envelope,
          entry: input.entry ?? 'typed',
          hasAttachment:
            Boolean(input.attachments?.length) ||
            Boolean(input.documentIds?.length),
        });
      }

      return dispatchTurn(
        {
          message,
          ...(effectiveConversationId
            ? { conversationId: effectiveConversationId }
            : {}),
          ...(!effectiveConversationId && forceNewConversation
            ? { forceNewConversation: true }
            : {}),
          ...(envelope ? { envelope } : {}),
          ...(input.attachments?.length
            ? { attachments: input.attachments }
            : {}),
          ...(input.documentIds?.length
            ? { documentIds: input.documentIds }
            : {}),
        },
        input.documentAttachments?.length
          ? {
              userMessageAttachments: [
                ...(input.attachments ?? []),
                ...input.documentAttachments,
              ],
            }
          : undefined
      );
    },
    [
      analyticsSurface,
      canOpenTurn,
      dispatchTurn,
      effectiveConversationId,
      forceNewConversation,
      getEnvelope,
    ]
  );

  const sendIfAccepted = useCallback(
    (input: ChatSendInput) => {
      const hasContent =
        input.message.trim() !== '' ||
        Boolean(input.attachments?.length) ||
        Boolean(input.documentIds?.length);

      if (!hasContent || !canOpenTurn()) return false;
      void send(input);
      return true;
    },
    [canOpenTurn, send]
  );

  const retry = useCallback(
    async (assistantMessageId: string) => {
      if (!canOpenTurn()) return null;

      const state = store.getState();
      const assistant = state.chat.messages[assistantMessageId];
      if (!assistant || assistant.role !== 'assistant') return null;

      const turn = assistant.turnId ? state.chat.turns[assistant.turnId] : null;

      // A turn this session names its own user message. Hydrated history has
      // no turn, so the question is the message that precedes the answer.
      const userMessage = turn
        ? state.chat.messages[turn.userMessageId]
        : findPrecedingUserMessage(
            state,
            assistant.conversationId,
            assistant.id
          );

      // Without a turn the retry needs the server's own id for `regenerateOf`;
      // a client id would mean nothing to the runtime.
      if (!userMessage || (!turn && !assistant.serverId)) return null;

      const attachmentRetry = planAttachmentRetry(userMessage);
      if (!attachmentRetry.canRetry) return null;

      // The re-run matches the depth the original turn ran at. Nothing sends
      // one any more (ORA-463 replaced "Go deeper" with runtime escalation),
      // so this is null on every turn this session started; it is kept because
      // the request field still exists and a hydrated turn may carry one.
      const depth = turn?.depth ?? null;

      /**
       * What the runtime is asked to replace: the latest PERSISTED answer of
       * this conversation, never a client-side id and never one an earlier
       * regenerate already superseded (ORA-459). A thread with nothing
       * persisted in it — a first turn that died before it wrote anything —
       * names nothing and the re-run goes out as a fresh ask.
       */
      const regenerateOf = selectRegenerateTargetId(
        state,
        assistant.conversationId
      );

      /**
       * And what leaves the thread: the bubble being re-run, plus the message
       * the request names when that is a different one (a retry of a failed
       * answer supersedes the last good one instead of itself). Both are what
       * the conversation read will hide, so both go now.
       */
      const named = regenerateOf
        ? Object.values(state.chat.messages).find(
            (candidate) =>
              candidate.conversationId === assistant.conversationId &&
              candidate.serverId === regenerateOf
          )
        : undefined;

      const supersedes = [
        assistant.id,
        ...(named && named.id !== assistant.id ? [named.id] : []),
      ];

      // No envelope here on purpose: `sendChatTurn` replays the one the
      // original turn was sent with, so a regenerate re-asks from where the
      // question was asked rather than from wherever the student has since
      // wandered — and it does not consume a selection made in the meantime.
      return dispatchTurn(
        {
          message: userMessage.text,
          conversationId: assistant.conversationId,
          ...(attachmentRetry.attachments.length
            ? { attachments: attachmentRetry.attachments }
            : {}),
          ...(attachmentRetry.documentIds.length
            ? { documentIds: attachmentRetry.documentIds }
            : {}),
          ...(depth ? { depth } : {}),
          ...(regenerateOf ? { regenerateOf } : {}),
        },
        { userMessageId: userMessage.id, supersedes }
      );
    },
    [canOpenTurn, dispatchTurn, store]
  );

  const stop = useCallback(() => {
    if (!streamingTurnId) return;
    dispatch(abortChatTurn(streamingTurnId));
  }, [dispatch, streamingTurnId]);

  return {
    conversationId: effectiveConversationId,
    send,
    sendIfAccepted,
    retry,
    stop,
    isStreaming: streamingTurnId !== null,
    isBusy: streamingTurnId !== null || isTurnSettling || isThreadResolving,
    isLimitReached,
    sendRejection,
  };
};
